import { createContext, useContext, useEffect, useState } from 'react';

/**
 * Rediseño · Bloque G — ¿está abierto el teclado de la pantalla?
 *
 * Lo encontró la prueba en teléfono de P-070 (Android 16, 2026-10-07): al abrirse el teclado, la ventana
 * se encoge, la barra inferior va al pie de la ventana y sube con él, y se queda encima del campo en el
 * que se escribe. En «Nuevo producto» el precio se veía a medias. Mientras se escribe, la barra se
 * esconde (`shell.css`) y el campo se trae a la vista (`AppShell`).
 *
 * Ningún navegador dice si el teclado está abierto. Se deduce de dos cosas a la vez:
 *   · la ventana es al menos `KEYBOARD_MIN_PX` más baja que la más alta vista con este mismo ancho, **o el
 *     `visualViewport` pierde por abajo al menos esa medida** (P-079: Chrome Android 108+ y Firefox Android
 *     132+ no encogen la ventana, solo recortan el área visible: `interactive-widget=resizes-visual`), y
 *   · al encogerse, el foco estaba en un campo de escribir (no en una fecha, una casilla o un botón).
 * Una sola no basta. El Atrás de Android cierra el teclado sin quitarle el foco al campo, y entonces la
 * barra tiene que volver. Y la ventana también cambia de alto por otras razones: al girar cambia el
 * ancho, y cada ancho lleva su propia medida. Con un teclado físico la ventana no se encoge y la barra
 * no se esconde. Lo que lo cierra es que la ventana recupere su alto (o el área visible su medida), o que
 * pase `BLUR_SETTLE_MS` sin un campo de escribir con el foco; no el instante en que el campo lo pierde.
 *
 * Solo usa `resize`, `focusin`, `focusout`, `innerHeight` y `visualViewport` (Chromium 61+): funciona en
 * Chromium 83, el WebView de Android 11.
 */
export const KEYBOARD_MIN_PX = 120;
/** Cuánto se espera, tras perder el foco, para dar el teclado por cerrado aunque la ventana siga baja. */
export const BLUR_SETTLE_MS = 600;
/**
 * P-079: con el documento ampliado (pellizco) más allá de esto, el recorte del `visualViewport` es del zoom,
 * no del teclado, y no se toma por tal. Limitación conocida: zoom y teclado a la vez no se corrigen.
 */
export const ZOOM_TOLERANCE = 1.01;

const TYPING_INPUTS = new Set(['text', 'search', 'email', 'tel', 'url', 'number', 'password']);

/** ¿Levanta el teclado este elemento con el foco? Un `<input>` sin `type` es de texto. */
export function isTypingTarget(element: Element | null): boolean {
  if (element instanceof HTMLTextAreaElement) return !element.readOnly && !element.disabled;
  if (element instanceof HTMLInputElement) return TYPING_INPUTS.has(element.type) && !element.readOnly && !element.disabled;
  return element instanceof HTMLElement && element.isContentEditable === true;
}

/** Qué parte de la ventana se ve de verdad, en píxeles de la ventana: de `top` a `bottom`; `inset` es lo que falta abajo. */
export interface VisibleViewport {
  top: number;
  bottom: number;
  inset: number;
}

/**
 * P-079 · La medida geométrica, separada de la heurística del teclado: con `visualViewport` (Chrome Android, Firefox
 * Android: el teclado recorta el área visible sin encoger la ventana), lo visible va de `offsetTop` a
 * `offsetTop + height` (Codex R1 · K2: el área visible también puede estar desplazada, no solo recortada por abajo).
 * Sin `visualViewport`, o con zoom, se ve la ventana entera.
 */
export function readVisibleViewport(): VisibleViewport {
  const height = window.innerHeight;
  const viewport = window.visualViewport;
  if (!viewport || (viewport.scale ?? 1) > ZOOM_TOLERANCE) return { top: 0, bottom: height, inset: 0 };
  const top = Math.max(0, Math.round(viewport.offsetTop));
  const bottom = Math.max(top, Math.min(height, Math.round(viewport.offsetTop + viewport.height)));
  return { top, bottom, inset: Math.max(0, height - bottom) };
}

export interface SoftKeyboardState {
  /** Según la heurística: la ventana o el área visible se encogieron mientras se escribía. */
  open: boolean;
  /** Lo que el área visible pierde por abajo (0 cuando la ventana entera se ve o cuando es la ventana la que se encoge). */
  inset: number;
}

export function useSoftKeyboard(): SoftKeyboardState {
  const [state, setState] = useState<SoftKeyboardState>({ open: false, inset: 0 });

  useEffect(() => {
    // El mayor alto visto con cada ancho: el de la ventana entera, con el teclado cerrado.
    const tallest = new Map<number, number>();
    const update = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const full = Math.max(tallest.get(width) ?? 0, height);
      tallest.set(width, full);
      const shrunk = full - height >= KEYBOARD_MIN_PX;
      const { inset } = readVisibleViewport();
      const cut = inset >= KEYBOARD_MIN_PX;
      // Se abre con un campo de escribir con el foco. Ya abierto, sigue así mientras la ventana (o el área visible) siga
      // encogida, aunque el foco se vaya: al tocar «Continuar» el campo pierde el foco antes de que el teclado baje, y
      // si la barra volviera en ese instante aparecería encima del botón que se está tocando.
      setState((was) => {
        const open = (shrunk || cut) && (was.open || isTypingTarget(document.activeElement));
        return was.open === open && was.inset === inset ? was : { open, inset };
      });
    };
    // Si al rato de perder el foco ya no hay campo de escribir con él, se da por cerrado aunque la ventana siga
    // baja: con la pantalla dividida de Android la ventana no recupera su alto, y la barra no puede quedarse
    // escondida para siempre. El rato basta para que el toque que se llevó el foco ya haya terminado.
    let blurTimer = 0;
    const afterBlur = () => {
      window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(() => { if (!isTypingTarget(document.activeElement)) setState((was) => (was.open ? { ...was, open: false } : was)); }, BLUR_SETTLE_MS);
    };
    const viewport = window.visualViewport;
    update();
    window.addEventListener('resize', update);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', afterBlur);
    return () => {
      window.clearTimeout(blurTimer);
      window.removeEventListener('resize', update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', afterBlur);
    };
  }, []);

  return state;
}

/** La heurística a secas (Bloque G): ¿está abierto el teclado? */
export function useSoftKeyboardOpen(): boolean {
  return useSoftKeyboard().open;
}

/**
 * El estado del teclado que mide el shell, para lo que vive dentro de él: el punto de venta esconde su barra
 * del pedido mientras se escribe, porque pegada abajo tapaba el buscador y los resultados (emulador y fotos
 * del SP01, P-070). Una sola medida —la del shell— y cada pantalla decide qué esconder en su propia hoja.
 * Fuera del shell, cerrado.
 */
export const SoftKeyboardContext = createContext(false);

/** ¿Está abierto el teclado, según el shell? */
export function useSoftKeyboardFromShell(): boolean {
  return useContext(SoftKeyboardContext);
}

/** Aire, en px, que se deja entre el campo y el borde al traerlo a la vista. */
export const REVEAL_MARGIN_PX = 12;

/** Los pies pegajosos que pueden tapar un campo dentro de su propio contenedor (P-079 · Codex R1 · K4). */
const STICKY_FEET = '.form-guided__footer, .n3-pos__foot, .n3-pos__bar';
/** La ayuda, el error o el eco que acompañan a un campo dentro de su propia etiqueta o envoltura. */
const COMPANIONS = 'small, .n3-field__hint, .n3-field__error, .cost-mode-hint, .wiz-echo';

const hasBox = (element: Element) => { const box = element.getBoundingClientRect(); return box.width > 0 || box.height > 0; };

/** ¿Es un contenedor que se desplaza verticalmente? Por clase (lo que una prueba sin maquetación puede saber) o por estilo. */
function isScroller(element: HTMLElement, shellScroller: HTMLElement | null): boolean {
  if (element === shellScroller || element.classList.contains('n3-dialog__body')) return true;
  const overflow = typeof getComputedStyle === 'function' ? getComputedStyle(element).overflowY : '';
  return overflow === 'auto' || overflow === 'scroll';
}

/**
 * Los contenedores que se desplazan alrededor de un elemento, de dentro hacia fuera. Dentro de una hoja fija
 * (`.n3-dialog`) lo de fuera no la mueve: el recorrido termina en ella. Fuera, termina en el área del shell.
 */
export function scrollAncestors(element: HTMLElement, shellScroller: HTMLElement | null): HTMLElement[] {
  const found: HTMLElement[] = [];
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (isScroller(node, shellScroller)) found.push(node);
    // El cuerpo de la hoja es el último contenedor que cuenta: la hoja es fija y lo de fuera no la mueve.
    if (node.classList.contains('n3-dialog__body') || node.classList.contains('n3-dialog') || node === shellScroller) break;
  }
  return found;
}

/**
 * Lo que acompaña a un campo y conviene ver con él: primero lo que el propio campo señala (`aria-describedby`),
 * y si no, la ayuda, el error o el eco que viene después del campo dentro de su misma etiqueta o envoltura.
 * Nada de fuera del campo (Codex R1 · K16): un aviso ajeno no es su compañero.
 */
export function fieldCompanion(field: HTMLElement): HTMLElement | null {
  const described = field.getAttribute('aria-describedby');
  if (described) {
    for (const id of described.split(/\s+/)) {
      const target = document.getElementById(id);
      if (target && hasBox(target)) return target;
    }
  }
  const wrap = field.closest<HTMLElement>('label, .field, .n3-field');
  if (!wrap) return null;
  const after = (candidate: Element) => Boolean(field.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING);
  return Array.from(wrap.querySelectorAll<HTMLElement>(COMPANIONS)).find((candidate) => after(candidate) && hasBox(candidate)) ?? null;
}

export interface RevealOptions {
  /** El área del shell que se desplaza (si el elemento no está en una hoja). */
  shellScroller?: HTMLElement | null;
  viewport?: VisibleViewport;
  /** Lo que se intenta ver junto con el elemento, si cabe (la ayuda o el error de un campo). */
  companion?: HTMLElement | null;
  margin?: number;
}

/**
 * Trae a la vista un elemento si quedó tapado. Lo mueve lo justo, con `REVEAL_MARGIN_PX` de aire, como el
 * navegador. Centrarlo movía la pantalla de más, y al cerrarse el teclado todo volvía a saltar. Si ya se ve
 * entero, nada se mueve.
 *
 * P-079: el área visible se recorta por el `visualViewport` (el teclado de Chrome/Firefox Android no encoge la
 * ventana); se recorre **cada** contenedor que se desplaza, de dentro hacia fuera, recalculando tras cada
 * movimiento (una hoja dentro de la página, un panel dentro de una hoja); un pie pegajoso que tape el campo
 * acorta el área; y el compañero del campo (ayuda, error, eco) se incluye cuando cabe. Nunca se mueve el shell
 * entero ni se añade relleno aquí: el espacio que el teclado quita lo reserva el CSS con `--n3-keyboard-inset`.
 */
export function revealElement(element: HTMLElement, options: RevealOptions = {}): void {
  const viewport = options.viewport ?? readVisibleViewport();
  const margin = options.margin ?? REVEAL_MARGIN_PX;
  const companion = options.companion ?? null;
  for (const scroller of scrollAncestors(element, options.shellScroller ?? null)) {
    const area = scroller.getBoundingClientRect();
    // Un pie pegajoso dentro de este contenedor que esté sobre el área visible tapa lo que quede debajo de su borde.
    const feet = Array.from(scroller.querySelectorAll<HTMLElement>(STICKY_FEET)).filter((foot) => !foot.contains(element) && hasBox(foot));
    const footTop = feet.reduce((lowest, foot) => Math.min(lowest, foot.getBoundingClientRect().top), Number.POSITIVE_INFINITY);
    // Los límites reales del área visible; el aire (`margin`) solo se aplica cuando hay que mover (Codex R2 · R5: un
    // campo que ya se ve entero pegado al borde no se mueve para ganar aire: eso era un movimiento artificial).
    const top = Math.max(area.top, viewport.top);
    const bottom = Math.min(area.bottom, viewport.bottom, footTop);
    const box = element.getBoundingClientRect();
    let target = { top: box.top, bottom: box.bottom };
    if (companion) {
      const extra = companion.getBoundingClientRect();
      const union = { top: Math.min(box.top, extra.top), bottom: Math.max(box.bottom, extra.bottom) };
      if (union.bottom - union.top <= bottom - top - 2 * margin) target = union;
    }
    if (target.top >= top && target.bottom <= bottom) continue;
    // Si no cabe entero, lo que se ve es su principio, que es donde se escribe.
    if (target.top < top || target.bottom - target.top > bottom - top - 2 * margin) scroller.scrollTop -= top + margin - target.top;
    else scroller.scrollTop += target.bottom - (bottom - margin);
  }
}

/**
 * Trae a la vista el campo con el foco si quedó tapado (con su ayuda o error, si caben). El WebView no siempre
 * lo hace solo: el campo puede estar dentro de un área con su propio desplazamiento (la del shell, el cuerpo
 * de una hoja). P-076: si el campo está dentro de una hoja (`.n3-dialog__body`), lo que se desplaza es el cuerpo
 * de la hoja, no el área del shell (la hoja es fija y el shell no la mueve).
 */
export function revealFocusedField(shellScroller: HTMLElement | null, viewport?: VisibleViewport): void {
  const field = document.activeElement;
  if (!(field instanceof HTMLElement) || !isTypingTarget(field)) return;
  revealElement(field, { shellScroller, viewport, companion: fieldCompanion(field) });
}
